/**
 * Чистая часть экрана входа в урок: состояния шагов превращаются в строки
 * списка, подпись под ним и решение показывать ли кнопку перезагрузки.
 *
 * Почему отдельным файлом и без единого импорта React. Тестов на компоненты в
 * проекте нет и не планируется (раздел 9 CLAUDE.md), поэтому граница проходит
 * ровно здесь: пороги и выбор строки проверяются `node:test`, а рисование
 * остаётся непроверяемым и потому обязано быть тривиальным. То же деление, что
 * у `audio-health.ts`.
 *
 * ⚠️ Тексты вычитаны и утверждены (раздел 4.4 `docs/room-preflight-design.md`).
 * Их читает школьник и его родитель: ни одного термина, ни слова про VPN.
 * Правка строки это правка документа, а не вкусовщина по месту.
 */

import type { EntryStepName } from "@edu/shared";

/**
 * Шаги, которые видит человек. Их три, а не четыре.
 *
 * ⚠️ Шага `audio` в списке НЕТ намеренно (4.4 проекта): он не завершился бы
 * здесь никогда, потому что комната открывается по готовности доски, а оверлей
 * к этому моменту уже снят. Про звук говорит своя полоса (`AudioTrouble`), у
 * неё для этого есть всё. В телеметрию шаг `audio` при этом уезжает: там он
 * отвечает на вопрос «а поднялось ли вообще», и никого не задерживает.
 */
export type ShownStep = Exclude<EntryStepName, "audio">;

export const SHOWN_STEPS: readonly ShownStep[] = ["access", "room-chunk", "board"];

/**
 * Состояние шага.
 *
 * `wait` это «ещё не начинали», а не «висит»: до того, как приедет чанк
 * комнаты, доску подключать не к чему.
 */
export type StepState = "wait" | "run" | "ok" | "fail";

const STEP_LABEL: Record<ShownStep, string> = {
  access: "Проверяем доступ",
  "room-chunk": "Загружаем комнату",
  board: "Подключаем доску",
};

/**
 * У какого шага молчание двадцати секунд что-то доказывает.
 *
 * ⚠️ У `room-chunk` не доказывает ничего, и это главное решение файла.
 * Комментарий в самом коде (`LessonPage.tsx`) говорит: чанк комнаты это
 * порядка мегабайта после gzip, «при 128 кбит/с это минуты». Единственный
 * измеримый признак прогресса у нас это доехавшие ресурсы (Resource Timing
 * отдаёт запись только по завершении файла), а деплой отдаёт комнату
 * несколькими крупными файлами параллельно: они финишируют кучно и в конце,
 * то есть в середине нормального скачивания «байты не прибавились двадцать
 * секунд» это обычное состояние, а не поломка. Предложить там кнопку значит
 * убить наполовину доехавший чанк и начать его заново у ровно того ученика,
 * ради которого всё затевалось.
 *
 * Про настоящую поломку чанка мы узнаём не таймером, а отказом самого
 * `import()`: он приезжает состоянием `fail`, и кнопка появляется от него.
 *
 * У `access` и `board` молчание доказывает: там на проводе один короткий ответ
 * API и один сокет, и двадцать секунд тишины это тишина, а не медленная
 * доставка.
 */
const STALLABLE: Record<ShownStep, boolean> = {
  access: true,
  "room-chunk": false,
  board: true,
};

/** Через сколько без движения незавершённый шаг помечается «долго». */
export const SLOW_MS = 8_000;

/** Через сколько без движения предлагаем перезагрузиться. */
export const STALL_MS = 20_000;

/**
 * Тайм-аут запросов шага «Проверяем доступ».
 *
 * Ровно `STALL_MS`, и это не совпадение: к той же секунде, когда экран решает
 * сказать «что-то не доехало», запрос обязан уже сдаться, иначе он висел бы
 * бесконечно за спиной у экрана, который про него уже всё сказал. Больше этот
 * тайм-аут не ставится нигде: загрузка материала на 128 кбит/с идёт минутами, и
 * общий потолок оборвал бы нормальную работу.
 */
export const ENTRY_TIMEOUT_MS = STALL_MS;

export const ENTRY_TITLE = "Открываем урок";

export const SLOW_TEXT =
  "Загружается дольше обычного. Это бывает на медленном интернете, подождите ещё немного.";

export const STALL_TEXT = "Что-то не доехало. Можно попробовать ещё раз.";

export const RELOAD_LABEL = "Обновить страницу";

/**
 * Что показывает `NoVideoNotice`, когда токен приехал, а соединение не встало
 * (2.6 проекта).
 *
 * Отдельной константой, а не литералом по месту: строку выбирает `LessonRoom`,
 * а вычитывают её здесь, рядом с остальными строками входа.
 */
export const CONNECT_FAILED_TEXT = "Звук и видео не подключились. Урок идёт на доске.";

/**
 * То же место, но когда в комнате не осталось мест.
 *
 * ⚠️ **Без своей строки шестой участник читает про сеть** (условие 7 рубежа
 * включения, `docs/group-room-design.md`, раздел 17). «Звук и видео не
 * подключились» это утверждение про интернет человека, а интернет у него в
 * порядке, и он пойдёт его чинить: перезагрузит страницу, перейдёт на Wi-Fi,
 * напишет преподавателю посреди занятия. Молчаливую и тем более ложную
 * деградацию запрещает раздел 5.5 CLAUDE.md.
 *
 * Читает это ребёнок или родитель, поэтому строка называет ровно два факта:
 * почему не вышло и что при этом работает.
 */
export const ROOM_FULL_TEXT =
  "В звонке заняты все места. Доска работает: вам видно всё, что пишет преподаватель.";

export interface EntryRow {
  step: ShownStep;
  label: string;
  /** Состояние словом: «готово», «идёт», «ждём», «долго», «не вышло». */
  word: string;
}

export interface EntryView {
  title: string;
  rows: EntryRow[];
  /** Подпись под списком. `null` значит «сказать нечего, всё идёт как обычно». */
  hint: string | null;
  /** Показывать ли кнопку перезагрузки. */
  reload: boolean;
}

const WORD_WAIT = "ждём";
const WORD_OK = "готово";
const WORD_FAIL = "не вышло";
const WORD_RUN = "идёт";
const WORD_SLOW = "долго";

/**
 * Список шагов, подпись и решение про кнопку.
 *
 * ⚠️ **Оба порога считаются от последнего движения, а не от начала загрузки.**
 * Абсолютного таймера здесь нет ни одного, и это исправление редакции 1
 * проекта: любой абсолютный порог назвал бы поломкой нормальное скачивание на
 * 128 кбит/с. `msSinceProgress` это «сколько миллисекунд не прибавлялось
 * байт», считает его вызывающий.
 *
 * ⚠️ Кнопка перезагрузки появляется по одной из двух причин, и обе это
 * положительное свидетельство поломки, а не истёкшее время само по себе:
 * шаг отказал, либо шаг молчит двадцать секунд И у этого шага молчание
 * что-то доказывает (см. `STALLABLE`). Пока хоть один незавершённый шаг
 * недоказуем, кнопки нет: полумегабайтный чанк не должен получать кнопку
 * «убей меня и начни сначала».
 */
export function entryView(input: {
  steps: Readonly<Record<ShownStep, StepState>>;
  msSinceProgress: number;
}): EntryView {
  const slow = input.msSinceProgress >= SLOW_MS;
  const running = SHOWN_STEPS.filter((s) => input.steps[s] === "run");
  const failed = SHOWN_STEPS.some((s) => input.steps[s] === "fail");

  const stalled =
    running.length > 0 &&
    running.every((s) => STALLABLE[s]) &&
    input.msSinceProgress >= STALL_MS;

  const reload = failed || stalled;

  const rows = SHOWN_STEPS.map((step) => ({
    step,
    label: STEP_LABEL[step],
    word: wordOf(input.steps[step], slow),
  }));

  return {
    title: ENTRY_TITLE,
    rows,
    hint: reload ? STALL_TEXT : running.length > 0 && slow ? SLOW_TEXT : null,
    reload,
  };
}

function wordOf(state: StepState, slow: boolean): string {
  if (state === "ok") return WORD_OK;
  if (state === "fail") return WORD_FAIL;
  if (state === "wait") return WORD_WAIT;
  return slow ? WORD_SLOW : WORD_RUN;
}

/** Все ли показываемые шаги завершились хоть чем-нибудь. */
export function allShownSettled(steps: Readonly<Record<ShownStep, StepState>>): boolean {
  return SHOWN_STEPS.every((s) => steps[s] === "ok" || steps[s] === "fail");
}

/**
 * Имена отказов `livekit-client`, означающие, что комната НЕ поднялась.
 *
 * ⚠️ Список нужен потому, что `onError` у `LiveKitRoom` это одна воронка на две
 * совершенно разные беды (проверено по коду `@livekit/components-react@2.9.23`,
 * `room-BC_ml4G1.mjs`): туда уходит и отказ `room.connect()`, и отказ публикации
 * своих дорожек на `SignalConnected`, то есть обычный отказ микрофона. Первое
 * означает «звука и видео не будет вовсе», второе означает «комната есть, а
 * микрофон занят», и лечатся они разными словами в разных местах экрана.
 *
 * ⚠️ Список белый, а не чёрный, и это осознанная осторожность. Ошибиться в
 * сторону «это отказ соединения» дороже: комната в этой ветке разбирается и
 * пересобирается без LiveKit, то есть ложное срабатывание отняло бы работающее
 * видео. Ошибка в другую сторону оставляет всё как сегодня, то есть ничего не
 * ломает. Поэтому голое `Error` (им пакет отвечает на «no livekit url
 * provided») сюда НЕ попадает: под этим именем приезжает и отказ публикации.
 */
const CONNECT_FAILURES: readonly string[] = [
  "ConnectionError",
  "UnsupportedServer",
  "SignalRequestError",
  "SignalReconnectError",
  "UnexpectedConnectionState",
  "NegotiationError",
];

/** Означает ли имя отказа, что комната не поднялась. */
export function isConnectFailure(name: string): boolean {
  return CONNECT_FAILURES.includes(name);
}

/**
 * Приметы отказа «в комнате нет мест» в тексте ошибки медиасервера.
 *
 * ⚠️ **Отказ приходит от LiveKit при подключении, а не от нашей ручки**, и
 * своего кода у него в нашем словаре нет и быть не может: наша ручка токен
 * ВЫДАЛА, вместимость проверяет SFU в момент входа в комнату. Значит различать
 * его приходится по тому, что прислал сервер, а прислать он может только текст.
 *
 * ⚠️ **Список примет ПРОВЕРЕН НЕ БЫЛ**, и это записано здесь честно: живого
 * шестого участника у нас ещё не было, а поднять его можно только на роли
 * `media`, у которой процедуры выкатки не существует (вопрос 5 проекта). Сверка
 * стоит той же строкой работ, что и замер `lk load-test` (раздел 13). Цена
 * ошибки при этом односторонняя и малая: не узнав примету, человек прочитает
 * прежний текст про звук и видео, то есть ровно то, что было до этой правки.
 * Поэтому и приметы разные по форме: сообщение сервера может приехать и
 * человеческой фразой, и константой протокола.
 */
const ROOM_FULL_MARKS: readonly string[] = [
  "room is full",
  "max participants",
  "maxparticipants",
  "max_participants",
  "room_full",
];

/**
 * Отказ соединения означает, что мест в комнате не осталось.
 *
 * Имя проверяется первым и по тому же белому списку, что у `isConnectFailure`:
 * отказ публикации микрофона про вместимость комнаты не говорит ничего, а
 * текст у него бывает какой угодно.
 */
export function isRoomFull(name: string, message: string): boolean {
  if (!isConnectFailure(name)) return false;
  const text = message.toLowerCase();
  return ROOM_FULL_MARKS.some((mark) => text.includes(mark));
}

/** Что показать вместо плиток, когда соединение с комнатой не встало. */
export function connectFailedText(args: { name: string; message: string }): string {
  return isRoomFull(args.name, args.message) ? ROOM_FULL_TEXT : CONNECT_FAILED_TEXT;
}
